import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { fetchMembershipsAndStations, type MembershipsAndStationsData } from "../services/memberships.service";
import { useFeedbackToast } from "@/hooks/useFeedbackToast";
import { queryKeys } from "@/lib/queryKeys";
import { useErrorHandler } from "@/contexts/errorHandler";
import { translateSupabaseError } from "@/utils/errorHandlerUtils";

// Referencias estables: un `[]` nuevo por render rompía la memoización de
// quien depende de estas listas mientras carga.
const EMPTY_MEMBERSHIPS: MembershipsAndStationsData["memberships"] = [];
const EMPTY_STATIONS: MembershipsAndStationsData["stations"] = [];

export function useMemberships() {
  const { showFeedback } = useFeedbackToast();
  const { logError } = useErrorHandler();

  const {
    data,
    isLoading: isDataLoading,
    isError,
    error,
  } = useQuery<MembershipsAndStationsData, Error>({
    queryKey: queryKeys.userMemberships(),
    queryFn: async () => {
      const response = await fetchMembershipsAndStations();
      if (response.status === 'error') {
        throw new Error(response.error?.message || 'No se pudieron cargar tus estaciones.');
      }
      return response.data || { memberships: [], stations: [] };
    },
    staleTime: 1000 * 60 * 5,
    // Sin reintentos: el error se avisa una sola vez con un toast.
    retry: false,
  });

  useEffect(() => {
    if (isError && error) {
      logError(error, {
        module: 'useMemberships',
        operation: 'fetchMembershipsAndStations'
      });
      showFeedback("error", translateSupabaseError(error, "Error al cargar accesos. Intenta recargar o contacta soporte.").message);
    }
  }, [isError, error, logError, showFeedback]);

  return {
    memberships: data?.memberships ?? EMPTY_MEMBERSHIPS,
    stations: data?.stations ?? EMPTY_STATIONS,
    isDataLoading,
    isError,
  };
}
